import { ApiErrorResponseWithCode, RequestParams, RequestResponse, requestJson } from '@vfde-sails/utils';
import { put, call, takeLatest, select } from 'redux-saga/effects';
import { getCaptcha, handleCaptchaResponse, handleLMSResponse } from './action';
import { mapFormValues } from './helpers/saga.helper';
import { Address, CaptchaState, FormDataType, SalesLeadErrorResponse, SalesLeadResponse } from './interface';
import { selectAddress, selectCaptcha, selectFormData } from './selector';
import { Endpoint } from '../../enum';
import { ActionType } from './enum';
import { getRequestApiKey, getRequestURL } from '../../helpers/request.helper';
import { debugInfo } from '../../utils/debug';
import { isApiError } from '../../helpers/objectValidator.helper';

/** Send a get request for a captcha image */
export function* getCaptchaSaga ({ captchaMediaType }: ReturnType<typeof getCaptcha>) {
    try {
        const request: RequestInit = {
            headers: {
                Accept: captchaMediaType,
                'x-api-key': getRequestApiKey(),
                'Accept-Language': 'DE',
            },
            method: 'GET',
        };
        // Note: sails core requestJSON won't work here because we cannot parse the reponse
        const response: Response = yield call(fetch, getRequestURL(Endpoint.CAPTCHA_API), request);

        // Note: with fetch catch is not triggered automatically
        if (200 !== response.status) {
            throw {
                status: response.status,
                statusText: response.statusText,
                body: null,
                message: response.statusText,
            } as ApiErrorResponseWithCode<null>;
        }

        const blob: Blob = yield response.blob();

        yield put(handleCaptchaResponse({
            validatorUrl: URL.createObjectURL(blob),
            captchaMediaType,
            hash: response.headers.get('x-captcha'),
        }));
    } catch (error) {
        const errorResponse = isApiError(error)
            ? error as ApiErrorResponseWithCode
            : {
                status: 0,
                statusText: 'Failed to fetch Captcha API',
                body: null,
                message: 'Failed to fetch Captcha API',
            } as ApiErrorResponseWithCode<null>;

        debugInfo('Capcha API error: ', errorResponse);

        yield put(handleCaptchaResponse(null, errorResponse));
    }
}

export function* submitFormSaga() {
    try {
        const { answer, hash }: CaptchaState = yield select(selectCaptcha());
        const rawFormData: FormDataType = yield select(selectFormData());
        const address: Address = yield select(selectAddress());
        const request: RequestParams = {
            method: 'POST',
            headers: {
                'X-Captcha-Answer': answer ?? '',
                'X-Captcha-Hash': hash ?? '',
                'x-api-key': getRequestApiKey(),
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(mapFormValues(rawFormData, address)),
        };
        const { status, body }: RequestResponse<SalesLeadResponse> =
            yield call(requestJson, getRequestURL(Endpoint.LMS_API).toString(), request);

        debugInfo('SalesLead API response: ', body);

        if (201 === status) {
            yield put(handleLMSResponse(body));
        }
    } catch (error) {
        const { body } = error as ApiErrorResponseWithCode<SalesLeadErrorResponse>;

        debugInfo('SalesLead API error: ', body);

        yield put(handleLMSResponse(null, body));
    }
}

export default function* appSaga() {
    yield takeLatest(ActionType.GET_CAPTCHA, getCaptchaSaga);
    yield takeLatest(ActionType.SUBMIT_FORM, submitFormSaga);
}
